import { Box, Button, Chip, FormControl, Grid, MenuItem, Select, Typography } from '@mui/material';
import ButtonSwitcher from 'components/ButtonSwitcher';
import React, { useState, useEffect, useCallback } from 'react';
import { injectBLSelectedWorkunitsFilterAction } from 'store/actions/catalogToOrderActions';
import { tagFilterBaseLineAction } from 'store/actions/changeBaselineActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';

type Tag = {
  id: string;
  label: string;
  enabled: boolean;
  color: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
};

type DisplayMode = 'BEFORE_CHANGE' | 'CHANGE' | 'AFTER_CHANGE' | 'NEW_BASELINE' | null;
type TimePeriod = 'MONTHLY' | 'QUARTERLY';

export default function WorkunitFilterSystem(props: any) {
  const { selectedScopeFilter } = props;
  const { dispatch } = useAppState();
  const selectedWorkunits = useSelector((state) => state.catalogToOrder.selectedWorkunits);

  const [tags, setTags] = useState<Tag[]>([
    { id: 'lastBaseline', label: 'Last Baseline', enabled: true, color: 'primary' },
    { id: 'added', label: 'Added', enabled: true, color: 'primary' },
    { id: 'cancelled', label: 'Cancelled', enabled: true, color: 'primary' },
    { id: 'newwu', label: 'New WU', enabled: true, color: 'primary' },
    { id: 'deleted', label: 'Deleted', enabled: false, color: 'primary' },
    { id: 'custom', label: 'Custom', enabled: true, color: 'primary' },
  ]);

  const [displayMode, setDisplayMode] = useState<DisplayMode>('AFTER_CHANGE');

  // Define tag combinations for each display mode
  const displayModeTagCombinations = {
    BEFORE_CHANGE: ['lastBaseline', 'added', 'cancelled', 'custom', 'deleted'],
    CHANGE: ['newwu', 'deleted'],
    AFTER_CHANGE: ['lastBaseline', 'added', 'cancelled', 'newwu', 'custom'],
    NEW_BASELINE: ['lastBaseline', 'newwu'],
  };

  // Memoize the filter function to avoid stale closures
  const filterData = useCallback(() => {
    const enabledTags = tags.filter((tag) => tag.enabled).map((tag) => tag.id);

    // Based on Scope filter in list
    const updated = selectedWorkunits.filter(
      (item) => selectedScopeFilter === 0 || item.scope_id === selectedScopeFilter
    );

    if (enabledTags.length === 0) {
      dispatch(injectBLSelectedWorkunitsFilterAction(updated));
      return;
    }

    const labelToTagMap: { [key: string]: string } = {
      quote: 'lastBaseline',
      added: 'added',
      cancelled: 'cancelled',
      custom: 'custom',
      newwu: 'newwu',
      deleted: 'deleted',
    };

    const filtered = updated.filter((item) => {
      const rawLabel = item?.label || '';
      const labels = rawLabel.split('|').map((tag) => tag.trim().toLowerCase());

      const lastLabel = labels[labels.length - 1] || '';
      // Special case: if ONLY one tag is enabled, apply that tag’s special logic
      if (enabledTags.length === 1) {
        const onlyTag = enabledTags[0];

        if (onlyTag === 'lastBaseline') {
          // Show rows containing 'quote' anywhere
          return labels.includes('quote');
        }

        if (onlyTag === 'cancelled') {
          // Show rows whose last label is cancelled
          return labels.includes('cancelled');
        }

        if (onlyTag === 'deleted') {
          // Show rows whose last label is deleted
          return labels.includes('deleted');
        }

        if (onlyTag === 'added') {
          // Show items explicitly marked 'added' OR marked 'new wu'
          return labels.includes('added');
        }

        if (onlyTag === 'custom') {
          // Show items explicitly marked 'added' OR marked 'new wu'
          return labels.includes('custom');
        }

        // Otherwise, filter by last label mapping
        const normalized = lastLabel === 'new wu' ? 'newwu' : lastLabel;
        const itemTag = labelToTagMap[normalized];
        return itemTag === onlyTag;
      }

      // If multiple tags enabled, include if ANY of the labels correspond to enabled tags
      const normalizedLabels = labels.map((lbl) => (lbl === 'new wu' ? 'newwu' : lbl));
      const itemTags = normalizedLabels.map((lbl) => labelToTagMap[lbl]).filter(Boolean);

      // if (labels.includes('deleted') && enabledTags[1] === 'newwu' && enabledTags.length === 2) {
      //   // Show rows containing 'quote' and not 'deleted'
      //   return false;
      // }
      if (labels.includes('deleted') && enabledTags.length > 1 && !enabledTags.includes('deleted')) {
        return false;
      }

      // Include if any tag in the row matches any enabled tag
      return itemTags.some((tag: string) => enabledTags.includes(tag));
    });

    dispatch(injectBLSelectedWorkunitsFilterAction(filtered));
  }, [tags, selectedWorkunits, selectedScopeFilter]); // Add both dependencies

  // Update filtered data when selectedWorkunits changes
  useEffect(() => {
    filterData();
  }, [filterData, tags, selectedWorkunits, selectedScopeFilter]); // Use the memoized function

  useEffect(() => {
    const enabledItems = tags.filter((item) => item.enabled === true);
    dispatch(tagFilterBaseLineAction(enabledItems));
  }, [tags, selectedScopeFilter]);
  // Toggle individual tag
  const handleToggleTag = (tagId: string) => {
    setTags((prevTags) =>
      prevTags.map((tag) => {
        // If this is the clicked tag, ensure it is enabled (prevents unselecting it)
        if (tag.id === tagId) {
          return { ...tag, enabled: true };
        }

        // If the code reaches here, it means tag.id !== tagId.
        // Therefore, this tag must be disabled (unselected).
        return { ...tag, enabled: false };
      })
    );
  };

  // Handle display mode change
  const handleDisplayModeChange = (mode: DisplayMode) => {
    if (mode === displayMode) {
      // If clicking the same mode, deactivate it
      setDisplayMode(null);
      setTags((prevTags) => prevTags.map((tag) => ({ ...tag, enabled: false })));
    } else {
      setDisplayMode(mode);
      if (mode && displayModeTagCombinations[mode]) {
        const requiredTags = displayModeTagCombinations[mode];
        setTags((prevTags) =>
          prevTags.map((tag) => ({
            ...tag,
            enabled: requiredTags.includes(tag.id),
          }))
        );
      }
    }
  };

  // Check if current tag selection matches any display mode (REMOVED DUPLICATE)
  useEffect(() => {
    const enabledTagIds = tags.filter((tag) => tag.enabled).map((tag) => tag.id);

    const matchingMode = Object.entries(displayModeTagCombinations).find(
      ([mode, requiredTags]) =>
        enabledTagIds.length === requiredTags.length &&
        enabledTagIds.every((id) => requiredTags.includes(id)) &&
        requiredTags.every((id) => enabledTagIds.includes(id))
    );

    const newDisplayMode = matchingMode ? (matchingMode[0] as DisplayMode) : null;
    if (newDisplayMode !== displayMode) {
      setDisplayMode(newDisplayMode);
    }
  }, [tags, displayMode]);

  // Translation function
  const t = (key: string) => {
    const translations: { [key: string]: string } = {
      'Before Change': 'Before Change',
      'After Change': 'After Change',
      Change: 'Change',
      Monthly: 'Monthly',
      Quarterly: 'Quarterly',
    };
    return translations[key] || key;
  };

  return (
    <Box sx={{ p: 2, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Filter Tags */}
      <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{ mb: 3, flexWrap: 'wrap' }}>
        {/* Tag Chips */}
        <Grid item xs={12} sm="auto">
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {tags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.label}
                onClick={() => handleToggleTag(tag.id)}
                color={tag.enabled ? tag.color : 'default'}
                variant={tag.enabled ? 'filled' : 'outlined'}
                size="medium"
                sx={{
                  fontWeight: tag.enabled ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              />
            ))}
          </Box>
        </Grid>

        {/* Display Mode Buttons */}
        <Grid item>
          <ButtonSwitcher size="small">
            <Button
              variant={displayMode === 'BEFORE_CHANGE' ? 'contained' : 'outlined'}
              color={displayMode === 'BEFORE_CHANGE' ? 'primary' : 'neutral'}
              onClick={() => handleDisplayModeChange('BEFORE_CHANGE')}
              size="small"
            >
              {t('Before Change')}
            </Button>
            <Button
              variant={displayMode === 'CHANGE' ? 'contained' : 'outlined'}
              color={displayMode === 'CHANGE' ? 'primary' : 'neutral'}
              onClick={() => handleDisplayModeChange('CHANGE')}
              size="small"
            >
              {t('Change')}
            </Button>
            <Button
              variant={displayMode === 'AFTER_CHANGE' ? 'contained' : 'outlined'}
              color={displayMode === 'AFTER_CHANGE' ? 'primary' : 'neutral'}
              onClick={() => handleDisplayModeChange('AFTER_CHANGE')}
              size="small"
            >
              {t('After Change')}
            </Button>
            <Button
              variant={displayMode === 'NEW_BASELINE' ? 'contained' : 'outlined'}
              color={displayMode === 'NEW_BASELINE' ? 'primary' : 'neutral'}
              onClick={() => handleDisplayModeChange('NEW_BASELINE')}
              size="small"
            >
              {t('New Baseline')}
            </Button>
          </ButtonSwitcher>
        </Grid>
      </Grid>
    </Box>
  );
}
